// An agent's change to a board's instructions, on its line in Board activity (Features 4, 26 Oct, David: "the activity
// feed says what changed, with the lines added and taken out, and an Undo that puts the last version back"). Undo is the
// owner's own put from this page, a version of its own, and shows only while the change is still the one in use.

import { RotateCcw } from "lucide-react";
import { instructionsHash } from "../../shared/instructions";
import type { Board, InstructionsChange } from "../../shared/types";
import { commit } from "../state/actions";
import { get, toast, useStore } from "../state/store";
import { cx } from "../lib/util";

/** Where a change stands now: still in use (Undo), undone (its words before are back), or changed again since. */
export function changeState(c: InstructionsChange, board: Board | undefined): "undo" | "undone" | "since" | "gone" {
  if (!board || board.archived) return "gone";
  const now = board.instructions ?? null;
  if (now === c.before) return "undone";
  return instructionsHash(now ?? "") === c.after ? "undo" : "since";
}

/** Puts back the words before the change, as the owner's own save. False when there is nothing to undo now. */
export function undoChange(c: InstructionsChange): boolean {
  const board = get().boards.find((b) => b.id === c.board);
  if (changeState(c, board) !== "undo" || !board) return false;
  commit([{ op: "board.put", board: { ...board, instructions: c.before, instructionsDraft: board.instructionsDraft ?? null } }], "Undo the change to the instructions");
  toast({ level: "success", title: c.before ? "Put the instructions back as they were" : "Cleared the instructions again", body: "Every agent follows them from its next read.", key: `instr-undo:${c.board}` });
  return true;
}

/** The lines added and taken out, a few each way, and Undo. readOnly: the gallery's sample, or a visitor's copy. */
export function InstrChange({ change, readOnly }: { change: InstructionsChange; readOnly?: boolean }) {
  const board = useStore((s) => s.boards.find((b) => b.id === change.board));
  const state = changeState(change, board);
  const more = change.more[0] + change.more[1];
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div className="ic" onClick={stop} onKeyDown={stop}>
      {(change.added.length > 0 || change.removed.length > 0) && (
        <ul className="ic-lines">
          {change.removed.map((l, i) => (
            <li key={`r${i}`} className="ic-line is-out">
              <span className="ic-sign" aria-label="Taken out">
                −
              </span>
              <span className="ic-words">{l}</span>
            </li>
          ))}
          {change.added.map((l, i) => (
            <li key={`a${i}`} className="ic-line is-in">
              <span className="ic-sign" aria-label="Added">
                +
              </span>
              <span className="ic-words">{l}</span>
            </li>
          ))}
          {more > 0 && <li className="ic-more">and {more} more</li>}
        </ul>
      )}
      {!readOnly && state !== "gone" && (
        <span className={cx("ic-foot", state !== "undo" && "is-quiet")}>
          {state === "undo" ? (
            <button type="button" className="btn btn-xs ic-undo" data-interactive onClick={() => undoChange(change)}>
              <RotateCcw size={12} /> Undo
            </button>
          ) : state === "undone" ? (
            "Undone"
          ) : (
            "Changed again since"
          )}
        </span>
      )}
    </div>
  );
}
